import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Grid } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { EamMetricsResponse } from '../admin.kpi.api';

interface EamMetricsCardProps {
    metrics: EamMetricsResponse | null;
    loading: boolean;
}

const EamMetricsCard: React.FC<EamMetricsCardProps> = ({ metrics, loading }) => {
    // Prepare chart data - combine unified DAA with submission volume
    const chartData = useMemo(() => {
        if (!metrics) return [];

        const unifiedMap = new Map(
            metrics.unifiedDailyActiveAthletes.map((d) => [d.day, Number(d.dau_any_form) || 0]),
        );

        const rpeMap = new Map(
            metrics.dailySubmissionVolume.rpe.map((d) => [d.day, Number(d.submissions) || 0]),
        );

        const readinessMap = new Map(
            metrics.dailySubmissionVolume.readiness.map((d) => [d.day, Number(d.submissions) || 0]),
        );

        // Get all unique days and sort ascending
        const allDays = new Set([...unifiedMap.keys(), ...rpeMap.keys(), ...readinessMap.keys()]);

        return Array.from(allDays)
            .sort((a, b) => a.localeCompare(b))
            .slice(-14) // Last 14 days for the small chart
            .map((day) => ({
                day: new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                activeAthletes: unifiedMap.get(day) || 0,
                rpeSubmissions: rpeMap.get(day) || 0,
                readinessSubmissions: readinessMap.get(day) || 0,
            }));
    }, [metrics]);

    // Calculate summary metrics
    const summaryMetrics = useMemo(() => {
        if (!metrics) return null;

        // Debug: Log the first few days to determine sort order
        if (metrics.unifiedDailyActiveAthletes.length > 0) {
            console.log(
                'EAM Metrics - First 3 days in array:',
                metrics.unifiedDailyActiveAthletes
                    .slice(0, 3)
                    .map((d) => ({ day: d.day, dau: d.dau_any_form })),
            );
            console.log(
                'EAM Metrics - Last 3 days in array:',
                metrics.unifiedDailyActiveAthletes
                    .slice(-3)
                    .map((d) => ({ day: d.day, dau: d.dau_any_form })),
            );
            console.log('EAM Metrics - Date range:', metrics.dateRange);
            console.log('EAM Metrics - Today (local):', new Date().toISOString().split('T')[0]);
        }

        const latestUnified = metrics.unifiedDailyActiveAthletes[0];
        const latestReadiness = metrics.readinessScores[0];
        const latestRpe = metrics.rpeLoadMetrics.averages[0];

        // Calculate totals for the period
        const totalRpeSubmissions = metrics.dailySubmissionVolume.rpe.reduce(
            (sum, d) => sum + (d.submissions || 0),
            0,
        );
        const totalReadinessSubmissions = metrics.dailySubmissionVolume.readiness.reduce(
            (sum, d) => sum + (d.submissions || 0),
            0,
        );
        const totalPainSorenessSubmissions = metrics.dailySubmissionVolume.painSoreness.reduce(
            (sum, d) => sum + (d.submissions || 0),
            0,
        );

        // Average DAU over period
        const avgDau =
            metrics.unifiedDailyActiveAthletes.length > 0
                ? Math.round(
                      metrics.unifiedDailyActiveAthletes.reduce(
                          (sum, d) => sum + (Number(d.dau_any_form) || 0),
                          0,
                      ) / metrics.unifiedDailyActiveAthletes.length,
                  )
                : 0;

        return {
            latestDau: latestUnified?.dau_any_form || 0,
            avgDau,
            avgReadiness:
                latestReadiness?.avg_readiness != null
                    ? Number(latestReadiness.avg_readiness).toFixed(1)
                    : 'N/A',
            avgSleepQuality:
                latestReadiness?.avg_sleep_quality != null
                    ? Number(latestReadiness.avg_sleep_quality).toFixed(1)
                    : 'N/A',
            avgRpe: latestRpe?.avg_rpe != null ? Number(latestRpe.avg_rpe).toFixed(1) : 'N/A',
            avgDuration:
                latestRpe?.avg_duration != null ? Number(latestRpe.avg_duration).toFixed(0) : 'N/A',
            totalRpeSubmissions,
            totalReadinessSubmissions,
            totalPainSorenessSubmissions,
            topBodyPart: metrics.painSorenessDetail.mostReportedBodyParts[0]?.display_name || 'N/A',
        };
    }, [metrics]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress sx={{ color: '#a08060' }} />
            </Box>
        );
    }

    if (!metrics || !summaryMetrics) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
                sx={{ color: '#6b5d4f' }}
            >
                <Typography variant="h6">No Data Available</Typography>
            </Box>
        );
    }

    const MetricRow: React.FC<{ label: string; value: string | number; color?: string }> = ({
        label,
        value,
        color = '#fff',
    }) => (
        <Box display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color }}>
                {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
        </Box>
    );

    return (
        <Box>
            {/* Key Metrics Summary */}
            <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box
                            sx={{
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #4a3f35',
                                borderRadius: '8px',
                                p: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                                Daily Active Users - Yesterday
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#a08060' }}>
                                {summaryMetrics.latestDau}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box
                            sx={{
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #4a3f35',
                                borderRadius: '8px',
                                p: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                                Daily Active Users - Past 30 Days
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                {summaryMetrics.avgDau}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Submission Totals */}
            <Box sx={{ mb: 2, backgroundColor: '#1a1a1a', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#6b5d4f', mb: 1, display: 'block' }}>
                    Submissions ({metrics.dateRange.days} days)
                </Typography>
                <MetricRow
                    label="RPE Entries"
                    value={summaryMetrics.totalRpeSubmissions}
                    color="#4caf50"
                />
                <MetricRow
                    label="Readiness"
                    value={summaryMetrics.totalReadinessSubmissions}
                    color="#2196f3"
                />
                <MetricRow
                    label="Pain/Soreness"
                    value={summaryMetrics.totalPainSorenessSubmissions}
                    color="#ff9800"
                />
            </Box>

            {/* Latest Averages */}
            <Box sx={{ mb: 2, backgroundColor: '#1a1a1a', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#6b5d4f', mb: 1, display: 'block' }}>
                    Latest Day Averages
                </Typography>
                <MetricRow label="Readiness Score" value={summaryMetrics.avgReadiness} />
                <MetricRow label="Sleep Quality" value={summaryMetrics.avgSleepQuality} />
                <MetricRow label="RPE" value={summaryMetrics.avgRpe} />
                <MetricRow label="Session Duration" value={`${summaryMetrics.avgDuration} min`} />
                <MetricRow
                    label="Top Pain Area"
                    value={summaryMetrics.topBodyPart}
                    color="#ff6b6b"
                />
            </Box>

            {/* Mini Chart */}
            {chartData.length > 0 && (
                <Box sx={{ height: 120, mt: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: '#6b5d4f', mb: 1, display: 'block' }}
                    >
                        14-Day Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                            <XAxis
                                dataKey="day"
                                stroke="#a0a0a0"
                                tick={{ fill: '#a0a0a0', fontSize: 9 }}
                                interval="preserveStartEnd"
                            />
                            <YAxis stroke="#a0a0a0" tick={{ fill: '#a0a0a0', fontSize: 9 }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid #4a3f35',
                                    borderRadius: '4px',
                                    fontSize: 11,
                                }}
                                labelStyle={{ color: '#fff' }}
                            />
                            <Line
                                type="monotone"
                                dataKey="activeAthletes"
                                stroke="#a08060"
                                strokeWidth={2}
                                dot={false}
                                name="Active Athletes"
                            />
                            <Line
                                type="monotone"
                                dataKey="rpeSubmissions"
                                stroke="#4caf50"
                                strokeWidth={1}
                                dot={false}
                                name="RPE"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #2a2a2a', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                    {metrics.dateRange.start} to {metrics.dateRange.end}
                </Typography>
            </Box>
        </Box>
    );
};

export default EamMetricsCard;
